import * as yup from 'yup';
import {
  arrivalTimes,
  classLevels,
  departureTimes,
  isDepartureAfterArrival,
  modesOfTransport,
  otherGroupsFormEstablishmentTypes,
  schoolFormEstablishmentTypes,
  tourTypes,
  visitDays,
  distanceInKmOptions,
  typeOfAcademyOptions,
  picnicTimeSlots,
} from './data';

const schoolFormEstablishmentDetailsSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf([...schoolFormEstablishmentTypes.map((type) => type.value)])
    .required('type'),
  postalCode: yup.string().required('postalCode'),
  name: yup.string().required('name'),
  city: yup.string().required('city'),
  address: yup.string().required('address'),
  department: yup.string().required('department'),
  typeOfAcademy: yup.string().required('typeOfAcademy'),
  distanceInKm: yup
    .string()
    .oneOf(distanceInKmOptions.map((option) => option.value))
    .required('distanceInKm'),
  privateContract: yup.boolean().required('privateContract'),
  rep: yup.boolean().required('rep'),
});

const inchargePersonSchema = yup.object().shape({
  firstName: yup.string().required('firstName'),
  lastName: yup.string().required('lastName'),
  function: yup.string().required('function'),
  email: yup.string().email('wrongEmailFormat').required('email'),
  phone: yup.string().required('phone'),
});

const isValidPicnicTimeSlot = (value, testContext) => {
  const { visitDay } = testContext.parent;
  return picnicTimeSlots[visitDay]?.map((time) => time.value).includes(value);
};

const picnicTimeSlotSchema = yup.string().when('picnicOnSite', {
  is: (value) => value === true || value === 'true',
  then: () =>
    yup
      .string()
      .test('visitDay', 'picnicTimeSlot', isValidPicnicTimeSlot)
      .required('picnicTimeSlot'),
  otherwise: () => yup.string().notRequired(),
});

const isValidArrivalTime = (value, testContext) => {
  const { visitDay } = testContext.parent;
  return arrivalTimes[visitDay]?.map((time) => time.value).includes(value);
};

const isValidDepartureTime = (value, testContext) => {
  const { visitDay, arrivalTime } = testContext.parent;
  return (
    departureTimes[visitDay]?.map((time) => time.value).includes(value) &&
    isDepartureAfterArrival(arrivalTime, value)
  );
};

const schoolRegistrationFormClassSchema = yup.object().shape({
  name: yup.string().required('name'),
  level: yup
    .string()
    .oneOf(classLevels.map((level) => level.value))
    .required('level'),
  otherLevel: yup.string().when('level', {
    is: (level) => level === 'other',
    then: () => yup.string().required('otherLevel'),
    otherwise: () => yup.string().notRequired(),
  }),
  numberOfStudents: yup
    .number()
    .min(1, 'numberOfStudents')
    .max(50, 'numberOfStudents')
    .required('numberOfStudents'),
  sector: yup.string(),
  visitDay: yup
    .string()
    .oneOf(visitDays.map((day) => day.value))
    .required('visitDay'),
  arrivalTime: yup
    .string()
    .test('visitDay', 'arrivalTime', isValidArrivalTime)
    .required('arrivalTime'),
  departureTime: yup
    .string()
    .test('visitDay', 'departureTime', isValidDepartureTime)
    .required('departureTime'),
  modeOfTransport: yup
    .string()
    .oneOf(['', ...modesOfTransport.map((transport) => transport.value)])
    .notRequired(),
  otherModeOfTransport: yup.string().when('modeOfTransport', {
    is: (modeOfTransport) => modeOfTransport === 'other',
    then: () => yup.string().required('otherModeOfTransport'),
    otherwise: () => yup.string().notRequired(),
  }),
  picnicOnSite: yup.boolean().required('picnicOnSite'),
  picnicTimeSlot: picnicTimeSlotSchema,
  handicap: yup.boolean().required('handicap'),
  handicapDescription: yup.string().when('handicap', {
    is: (value) => value === true || value === 'true',
    then: () => yup.string().required('handicapDescription'),
    otherwise: () => yup.string().notRequired(),
  }),
  responsibleInformation: yup
    .object()
    .shape({
      firstName: yup.string(),
      lastName: yup.string(),
      function: yup.string(),
      email: yup.string().email('wrongEmailFormat'),
      phone: yup.string(),
    })
    .nullable(),
  bookExperience: yup.boolean().required('bookExperience'),
  experienceDetails: yup.object().shape({
    level: yup
      .string()
      .oneOf([...classLevels].map((level) => level.value))
      .required('level'),
    tourType: yup
      .string()
      .oneOf(
        [...tourTypes].map((tour) => tour.value),
        'correctTourType',
      )
      .required('tourType'),
    timeSlot: yup
      .string()
      .test('matchesArrivalTime', 'timeSlot', function (value) {
        const classValues = this.from?.[1]?.value;
        return value === classValues?.arrivalTime;
      })
      .required('timeSlot'),
  }),
});

const emailConsentSchema = yup.object().shape({
  emailConsent: yup.boolean().required('emailConsent'),
  studentsSafetyConsent: yup
    .mixed()
    .oneOf([true, 'true'], 'studentsSafetyConsent')
    .required('studentsSafetyConsent'),
  email: yup
    .string()
    .email('wrongEmailFormat')
    .when('emailConsent', {
      is: (value) => value === true || value === 'true',
      then: () => yup.string().email('wrongEmailFormat').required('email'),
      otherwise: () => yup.string().notRequired(),
    }),
});

export const schoolRegistrationFormSchema = yup.object().shape({
  establishmentDetails: schoolFormEstablishmentDetailsSchema,
  inchargePersonDetails: inchargePersonSchema,
  inchargePersonAnother: yup.boolean().required('inchargePersonAnother'),
  inchargePersonAnotherDetails: yup.object().when('inchargePersonAnother', {
    is: (value) => value === 'false' || value === false,
    then: () => inchargePersonSchema,
    otherwise: () => yup.object().nullable(),
  }),
  classes: yup.array().of(schoolRegistrationFormClassSchema),
  emailConsent: emailConsentSchema,
});

const otherGroupsFormEstablishmentDetailsSchema = yup.object().shape({
  type: yup
    .string()
    .oneOf([...otherGroupsFormEstablishmentTypes.map((type) => type.value)])
    .required('establishmentType'),
  otherType: yup.string().when('type', {
    is: (value) => value === 'autre',
    then: () => yup.string().required('otherType'),
    otherwise: () => yup.string().notRequired(),
  }),
  postalCode: yup.string().required('postalCode'),
  name: yup.string().required('name'),
  city: yup.string().required('city'),
  address: yup.string().required('address'),
  department: yup.string().required('department'),
  typeOfAcademy: yup
    .string()
    .oneOf(typeOfAcademyOptions.map((option) => option.value))
    .required('typeOfAcademy'),
  distanceInKm: yup
    .string()
    .oneOf(distanceInKmOptions.map((option) => option.value))
    .required('distanceInKm'),
});

const otherGroupsRegistrationFormGroupSchema = yup.object().shape({
  name: yup.string().required('name'),
  numberOfStudents: yup
    .number()
    .min(1, 'numberOfStudents')
    .max(50, 'numberOfStudents')
    .required('numberOfStudents'),
  visitDay: yup
    .string()
    .oneOf(visitDays.map((day) => day.value))
    .required('visitDay'),
  arrivalTime: yup
    .string()
    .test('visitDay', 'arrivalTime', isValidArrivalTime)
    .required('arrivalTime'),
  departureTime: yup
    .string()
    .test('visitDay', 'departureTime', isValidDepartureTime)
    .required('departureTime'),
  modeOfTransport: yup
    .string()
    .oneOf(modesOfTransport.map((transport) => transport.value))
    .required('modeOfTransport'),
  otherModeOfTransport: yup.string().when('modeOfTransport', {
    is: (modeOfTransport) => modeOfTransport === 'other',
    then: () => yup.string().required('otherModeOfTransport'),
    otherwise: () => yup.string().notRequired(),
  }),
  picnicOnSite: yup.boolean().required('picnicOnSite'),
  picnicTimeSlot: picnicTimeSlotSchema,
  handicap: yup.boolean().required('handicap'),
  handicapDescription: yup.string().when('handicap', {
    is: (value) => value === true || value === 'true',
    then: () => yup.string().required('handicapDescription'),
    otherwise: () => yup.string().notRequired(),
  }),
  responsibleInformation: yup
    .object()
    .shape({
      firstName: yup.string(),
      lastName: yup.string(),
      function: yup.string(),
      email: yup.string().email('wrongEmailFormat'),
      phone: yup.string(),
    })
    .nullable(),
});

const otherGroupsRegistrationFormClassSchema = yup.object().shape({
  name: yup.string().required('name'),
  level: yup.string().required('level'),
  numberOfStudents: yup
    .number()
    .min(1, 'numberOfStudents')
    .max(50, 'numberOfStudents')
    .required('numberOfStudents'),
  sector: yup.string(),
  visitDay: yup
    .string()
    .oneOf(visitDays.map((day) => day.value))
    .required('visitDay'),
  arrivalTime: yup
    .string()
    .test('visitDay', 'arrivalTime', isValidArrivalTime)
    .required('arrivalTime'),
  departureTime: yup
    .string()
    .test('visitDay', 'departureTime', isValidDepartureTime)
    .required('departureTime'),
  modeOfTransport: yup
    .string()
    .oneOf(modesOfTransport.map((transport) => transport.value))
    .required('modeOfTransport'),
  otherModeOfTransport: yup.string().when('modeOfTransport', {
    is: (modeOfTransport) => modeOfTransport === 'other',
    then: () => yup.string().required('otherModeOfTransport'),
    otherwise: () => yup.string().notRequired(),
  }),
  picnicOnSite: yup.boolean().required('picnicOnSite'),
  picnicTimeSlot: picnicTimeSlotSchema,
  handicap: yup.boolean().required('handicap'),
  handicapDescription: yup.string().when('handicap', {
    is: (value) => value === true || value === 'true',
    then: () => yup.string().required('handicapDescription'),
    otherwise: () => yup.string().notRequired(),
  }),
  responsibleInformation: yup
    .object()
    .shape({
      firstName: yup.string(),
      lastName: yup.string(),
      function: yup.string(),
      email: yup.string().email('wrongEmailFormat'),
      phone: yup.string(),
    })
    .nullable(),
});

export const otherGroupsRegistrationFormSchema = yup.object().shape({
  establishmentDetails: otherGroupsFormEstablishmentDetailsSchema,
  inchargePersonDetails: inchargePersonSchema,
  inchargePersonAnother: yup.boolean().required('inchargePersonAnother'),
  inchargePersonAnotherDetails: yup.object().when('inchargePersonAnother', {
    is: (value) => value === 'false' || value === false,
    then: () => inchargePersonSchema,
    otherwise: () => yup.object().nullable(),
  }),
  classes: yup.array().when('establishmentDetails.type', {
    is: (type) =>
      [...otherGroupsFormEstablishmentTypes]
        .slice(0, 2)
        .map((type) => type.value)
        .includes(type),
    then: () => yup.array().of(otherGroupsRegistrationFormClassSchema),
    otherwise: () => yup.array().of(otherGroupsRegistrationFormGroupSchema),
  }),
  emailConsent: emailConsentSchema,
});
